const DB_NAME = 'preform-models';
const STORE = 'models';
const KEY = 'current';
const CACHE_NAME = 'preform-models';
const CACHE_URL = '/__preform-saved-model.glb';

type CachedModel = {name: string;buffer: ArrayBuffer;};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveToIndexedDb(model: CachedModel): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(model, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function loadFromIndexedDb(): Promise<CachedModel | null> {
  const db = await openDb();
  const result = await new Promise<CachedModel | null>((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result as CachedModel ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

async function saveToCacheStorage(model: CachedModel): Promise<void> {
  const cache = await caches.open(CACHE_NAME);
  await cache.put(
    CACHE_URL,
    new Response(model.buffer, { headers: { 'Content-Type': 'model/gltf-binary', 'X-Model-Name': encodeURIComponent(model.name) } })
  );
}

async function loadFromCacheStorage(): Promise<CachedModel | null> {
  const cache = await caches.open(CACHE_NAME);
  const res = await cache.match(CACHE_URL);
  if (!res) return null;
  return { name: decodeURIComponent(res.headers.get('X-Model-Name') ?? 'model.glb'), buffer: await res.arrayBuffer() };
}

/** Saves the model in this browser. Returns true if at least one storage method succeeded. */
export async function saveCachedModel(name: string, buffer: ArrayBuffer): Promise<boolean> {
  navigator.storage?.persist?.().catch(() => undefined);
  const model = { name, buffer: buffer.slice(0) };
  const results = await Promise.allSettled([saveToIndexedDb(model), saveToCacheStorage(model)]);
  return results.some((r) => r.status === 'fulfilled');
}

export async function loadCachedModel(): Promise<CachedModel | null> {
  for (const load of [loadFromIndexedDb, loadFromCacheStorage]) {
    try {
      const model = await load();
      if (model && model.buffer.byteLength > 0) return model;
    } catch {
      // Storage may be blocked in sandboxed previews — try the next method.
    }
  }
  return null;
}
